import { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Chip } from '@material-ui/core'
import clsx from 'clsx'

import { TableRow, TableCell } from 'components/Table'
import { ConfirmationDialog, MenuButton, Permitted } from 'components'
import PaymentServiceDialog from '../PaymentServiceDialog'
import ServiceCompaniesDialog from '../ServiceCompaniesDialog'

import { usePermissions, useSnackbar } from 'hooks'
import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'

import useStyles from './styles'

const ServicesTableRow = ({
  paymentContractId,
  paymentService,
  refresh = () => {},
  isShow,
  refreshContract,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { permitted, some } = usePermissions()
  const [openChangeStatusDialog, setOpenChangeStatusDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openCompaniesDialog, setOpenCompaniesDialog] = useState(false)

  const canManage = permitted(constants.permissions.PAYMENT_CONTRACTS.MANAGE)
  const canDestroy =
    permitted(constants.permissions.PAYMENT_CONTRACTS.DESTROY_SERVICE) &&
    !paymentService?.isPlatformType
  const canListCompanies =
    paymentService?.isGovernanceReviewType &&
    some([
      constants.permissions.PAYMENT_CONTRACTS.LIST_SERVICE_COMPANIES,
      constants.permissions.PAYMENT_CONTRACTS.SELECT_SERVICE_COMPANY,
      constants.permissions.PAYMENT_CONTRACTS.UNSELECT_SERVICE_COMPANY,
    ])

  const handleActiveOrInactive = async () => {
    try {
      await service.dponet.paymentContractServices.changeStatus({
        paymentContractId,
        paymentServiceId: paymentService?.id,
      })
      snackbar.open({
        message: `Serviço ${
          paymentService?.active ? 'inativado' : 'ativado'
        } com sucesso!`,
        variant: 'success',
      })
      refresh()
      refreshContract()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Falha ao alterar o status do serviço.',
        variant: 'error',
      })
    }
    setOpenChangeStatusDialog(false)
  }

  const handleDelete = async () => {
    try {
      await service.dponet.paymentContractServices.destroy({
        paymentContractId,
        paymentServiceId: paymentService?.id,
      })
      snackbar.open({
        message: 'Serviço excluído com sucesso!',
        variant: 'success',
      })
      refresh()
      refreshContract()
    } catch (error) {
      snackbar.open({
        variant: 'error',
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Falha ao excluir o serviço.',
      })
    }
    setOpenDeleteDialog(false)
  }

  return (
    <TableRow>
      <TableCell align="left">#{paymentService?.id}</TableCell>
      <TableCell align="left">{paymentService?.name}</TableCell>
      <TableCell align="center">
        R$ {helpers.formatters.currencyInput(paymentService?.price)}
      </TableCell>
      <TableCell align="center">
        {paymentService?.endDate
          ? helpers.formatters.date(paymentService?.endDate)
          : '-'}
      </TableCell>
      <TableCell align="center">
        {paymentService?.renewable ? 'Sim' : 'Não'}
      </TableCell>
      <TableCell disableTooltip align="center">
        <Chip
          className={clsx(classes.root, {
            [classes.active]: paymentService?.active,
            [classes.inactive]: !paymentService?.active,
          })}
          size="small"
          label={paymentService?.active ? 'Ativo' : 'Inativo'}
        />
      </TableCell>
      {!isShow && (
        <TableCell disableTooltip align="right">
          <Permitted show={canManage || canDestroy || canListCompanies}>
            <MenuButton>
              {canListCompanies && (
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setOpenCompaniesDialog(true)}
                >
                  Empresas
                </Button>
              )}
              <Permitted tag={constants.permissions.PAYMENT_CONTRACTS.MANAGE}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setOpenEditDialog(true)}
                >
                  Editar
                </Button>
                <Button
                  className={clsx({
                    [classes.hidden]: paymentService?.isPlatformType,
                  })}
                  fullWidth
                  size="small"
                  onClick={() => setOpenChangeStatusDialog(true)}
                >
                  {paymentService?.active ? 'Inativar' : 'Ativar'}
                </Button>
              </Permitted>
              <Permitted
                tag={constants.permissions.PAYMENT_CONTRACTS.DESTROY_SERVICE}
              >
                <Button
                  className={clsx({
                    [classes.hidden]: paymentService?.isPlatformType,
                  })}
                  fullWidth
                  size="small"
                  onClick={() => setOpenDeleteDialog(true)}
                >
                  Excluir
                </Button>
              </Permitted>
            </MenuButton>
          </Permitted>
        </TableCell>
      )}
      {openChangeStatusDialog && (
        <ConfirmationDialog
          open={openChangeStatusDialog}
          setOpen={setOpenChangeStatusDialog}
          message={
            constants.paymentContract.SERVICE_DIALOG_CHANGE_STATUSES[
              paymentService?.active
            ]
          }
          cancelButtonText="Cancelar"
          buttonText={paymentService?.active ? 'Inativar' : 'Ativar'}
          actionAcceptButton={handleActiveOrInactive}
        />
      )}
      {openDeleteDialog && (
        <ConfirmationDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          message={constants.paymentContract.SERVICE_DESTROY_DIALOG}
          cancelButtonText="Cancelar"
          buttonText="Excluir"
          actionAcceptButton={handleDelete}
        />
      )}
      {openEditDialog && (
        <PaymentServiceDialog
          open={openEditDialog}
          setOpen={setOpenEditDialog}
          paymentContractId={paymentContractId}
          paymentService={paymentService}
          refresh={refresh}
          refreshContract={refreshContract}
        />
      )}
      {openCompaniesDialog && (
        <ServiceCompaniesDialog
          open={openCompaniesDialog}
          setOpen={setOpenCompaniesDialog}
          paymentContractId={paymentContractId}
          paymentService={paymentService}
        />
      )}
    </TableRow>
  )
}

ServicesTableRow.propTypes = {
  paymentContractId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  paymentService: PropTypes.object,
  refresh: PropTypes.func,
  isShow: PropTypes.bool,
}

export default ServicesTableRow
